import type { TimeGrid, Weekday } from './types.js';

/** Day 0 is Monday: the order the weekly-hours editor and a creator's week read in. */
export const WEEK_FROM_MONDAY: Weekday[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

/** One open range on one day of the week, in the creator's own wall-clock time. */
export interface WeekBlock {
  /** 0..6, Monday first. */
  day: number;
  /** HH:mm; the end may be 24:00. */
  start: string;
  end: string;
}

/**
 * The stored grid: each time is a moment on 1970-01-01 UTC, shifted back by the creator's
 * UTC offset. Decorator-free, so the weekly-hours editor and the connector write one shape.
 */
export function weekToGrid(blocks: WeekBlock[], utcOffsetMinutes: number): TimeGrid {
  const at = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return new Date(Date.UTC(1970, 0, 1, h, m) - utcOffsetMinutes * 60_000);
  };
  const grid: TimeGrid = {};
  for (const block of [...blocks].sort((a, b) => a.start.localeCompare(b.start))) {
    const day = WEEK_FROM_MONDAY[block.day];
    (grid[day] ??= []).push({ start: at(block.start), end: at(block.end) });
  }
  return grid;
}

const DAY_MS = 86_400_000;

/** The editor never shows 23:59: a range that ends there ran to midnight. */
function clockTime(msIntoDay: number): string {
  const minutes = Math.floor(msIntoDay / 60_000);
  if (msIntoDay >= DAY_MS || minutes === 23 * 60 + 59) return '24:00';
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * The stored grid read back as the creator's week, the inverse of `weekToGrid`. A range
 * that crosses the creator's midnight is split across the two days, except an end stored
 * as 23:xx UTC on day one, which older grids wrote for "until midnight".
 */
export function gridToWeek(
  grid: Partial<Record<Weekday, { start: Date | string; end: Date | string }[]>>,
  utcOffsetMinutes: number,
): WeekBlock[] {
  const shift = utcOffsetMinutes * 60_000;
  const parts: { day: number; start: number; end: number }[] = [];
  WEEK_FROM_MONDAY.forEach((name, day) => {
    const at = (offset: number, start: number, end: number) =>
      parts.push({ day: (day + offset + 7) % 7, start, end });
    for (const slot of grid[name] ?? []) {
      const startUtc = new Date(slot.start).getTime();
      const endUtc = new Date(slot.end).getTime();
      if (startUtc >= endUtc) continue;
      const start = startUtc + shift;
      const end = endUtc + shift;
      const startDay = Math.floor(start / DAY_MS);
      const endDay = Math.floor(end / DAY_MS);
      if (startDay === endDay && Math.abs(startDay) <= 1) {
        at(startDay, start - startDay * DAY_MS, end - startDay * DAY_MS);
      } else if (startDay === 0 && endDay === 1) {
        at(0, start, DAY_MS);
        const legacyMidnight = endUtc >= 23 * 3_600_000 && endUtc < DAY_MS;
        if (end > DAY_MS && !legacyMidnight) at(1, 0, end - DAY_MS);
      } else if (startDay === -1 && endDay === 0) {
        at(-1, start + DAY_MS, DAY_MS);
        at(0, 0, end);
      } else {
        const intoDay = (ms: number) => ((ms % DAY_MS) + DAY_MS) % DAY_MS;
        at(0, intoDay(start), intoDay(end));
      }
    }
  });
  return parts
    .sort((a, b) => a.day - b.day || a.start - b.start)
    .map(({ day, start, end }) => ({ day, start: clockTime(start), end: clockTime(end) }));
}

const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The week as a reader says it: consecutive days with the same hours fold into one line. */
export function weekText(blocks: WeekBlock[]): string[] {
  const hours = SHORT_DAYS.map((_, day) =>
    blocks
      .filter((b) => b.day === day)
      .sort((a, b) => a.start.localeCompare(b.start))
      .map((b) => `${b.start}–${b.end}`)
      .join(', '),
  );
  const lines: string[] = [];
  for (let day = 0; day < 7; day++) {
    if (!hours[day]) continue;
    let last = day;
    while (last + 1 < 7 && hours[last + 1] === hours[day]) last++;
    const days = last === day ? SHORT_DAYS[day] : `${SHORT_DAYS[day]}–${SHORT_DAYS[last]}`;
    lines.push(`${days} ${hours[day]}`);
    day = last;
  }
  return lines;
}

const HHMM = /^(?:[01]\d|2[0-3]):[0-5]\d$|^24:00$/;

/** Why a week cannot be saved, in words; null when it can. One rule for the connector and the web. */
export function weekProblem(blocks: WeekBlock[]): string | null {
  for (const b of blocks) {
    const day = WEEK_FROM_MONDAY[b.day];
    if (!day || !HHMM.test(b.start) || !HHMM.test(b.end)) return 'A time is not in HH:mm.';
    if (b.end !== '24:00' && b.start >= b.end) {
      return `On ${day}, ${b.start} to ${b.end} ends before it starts.`;
    }
  }
  // HH:mm compares as text, and 24:00 sorts last, so neighbours in start order show any overlap.
  const sorted = [...blocks].sort((a, b) => a.day - b.day || a.start.localeCompare(b.start));
  for (let i = 1; i < sorted.length; i++) {
    const [prev, next] = [sorted[i - 1], sorted[i]];
    if (prev.day === next.day && next.start < prev.end) {
      return `On ${WEEK_FROM_MONDAY[next.day]}, ${prev.start}–${prev.end} and ${next.start}–${next.end} overlap: give one range.`;
    }
  }
  return null;
}

/** The week a new page opens on when nobody named one: weekday evenings, 18:00 to 21:00. */
export const DEFAULT_CLAIM_WEEK: WeekBlock[] = [0, 1, 2, 3, 4].map((day) => ({
  day,
  start: '18:00',
  end: '21:00',
}));
